import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  DocumentTextIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ClipboardDocumentCheckIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  StarIcon,
  CalendarIcon,
  FolderIcon,
  ChartBarIcon,
  AdjustmentsHorizontalIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const MyForms = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('created');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('lastModified');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Simulate fetching user's forms data
    const fetchForms = () => {
      const mockForms = {
        created: [
          {
            id: 1,
            title: 'Patient Registration Form',
            description: 'Comprehensive patient registration form for new admissions',
            category: 'Patient Forms',
            status: 'active',
            version: '2.1',
            createdDate: '2025-01-20T10:00:00Z',
            lastModified: '2025-01-22T14:30:00Z',
            responses: 145,
            views: 289,
            fields: 12,
            estimatedTime: '10 minutes',
            isFavorite: true,
            tags: ['Registration', 'Patient', 'Medical History']
          },
          {
            id: 2,
            title: 'Incident Report Form',
            description: 'Report form for workplace incidents and safety concerns',
            category: 'Administrative',
            status: 'active',
            version: '3.0',
            createdDate: '2025-01-15T16:00:00Z',
            lastModified: '2025-01-16T09:15:00Z',
            responses: 23,
            views: 67,
            fields: 15,
            estimatedTime: '8 minutes',
            isFavorite: false,
            tags: ['Incident', 'Safety', 'Report']
          },
          {
            id: 3,
            title: 'Equipment Request Form',
            description: 'Form for requesting new medical equipment and supplies',
            category: 'Administrative',
            status: 'draft',
            version: '1.0',
            createdDate: '2025-01-12T11:30:00Z',
            lastModified: '2025-01-14T15:45:00Z',
            responses: 0,
            views: 12,
            fields: 10,
            estimatedTime: '5 minutes',
            isFavorite: false,
            tags: ['Equipment', 'Request', 'Medical']
          }
        ],
        collaborating: [
          {
            id: 4,
            title: 'Employee Onboarding Checklist',
            description: 'Complete checklist for new employee onboarding process',
            category: 'HR Forms',
            status: 'active',
            version: '1.5',
            createdDate: '2025-01-18T08:00:00Z',
            lastModified: '2025-01-19T12:20:00Z',
            createdBy: 'Jennifer Wu',
            role: 'Editor',
            responses: 34,
            views: 78,
            fields: 8,
            estimatedTime: '15 minutes',
            isFavorite: true,
            tags: ['HR', 'Onboarding', 'Checklist']
          },
          {
            id: 5,
            title: 'Clinical Trial Consent Form',
            description: 'Informed consent form for clinical trial participants',
            category: 'Research',
            status: 'review',
            version: '1.3',
            createdDate: '2025-01-08T14:00:00Z',
            lastModified: '2025-01-09T16:30:00Z',
            createdBy: 'Dr. Emily Davis',
            role: 'Reviewer',
            responses: 12,
            views: 34,
            fields: 20,
            estimatedTime: '20 minutes',
            isFavorite: false,
            tags: ['Research', 'Consent', 'Clinical Trial']
          }
        ],
        templates: [
          {
            id: 6,
            title: 'Basic Contact Form Template',
            description: 'Simple contact form template with essential fields',
            category: 'Templates',
            status: 'template',
            version: '1.0',
            createdDate: '2025-01-05T10:00:00Z',
            lastModified: '2025-01-05T10:00:00Z',
            uses: 15,
            fields: 6,
            estimatedTime: '3 minutes',
            isFavorite: false,
            tags: ['Template', 'Contact', 'Basic']
          },
          {
            id: 7,
            title: 'Feedback Survey Template',
            description: 'Comprehensive feedback survey template',
            category: 'Templates',
            status: 'template',
            version: '2.0',
            createdDate: '2025-01-03T09:00:00Z',
            lastModified: '2025-01-03T09:00:00Z',
            uses: 28,
            fields: 12,
            estimatedTime: '8 minutes',
            isFavorite: true,
            tags: ['Template', 'Survey', 'Feedback']
          }
        ]
      };

      setForms(mockForms);
      setLoading(false);
    };

    setTimeout(fetchForms, 500);
  }, []);

  const getCurrentForms = () => {
    return forms[activeTab] || [];
  };

  const filteredForms = getCurrentForms().filter(form => {
    const matchesSearch = form.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         form.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         form.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || form.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const sortedForms = [...filteredForms].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'category':
        comparison = a.category.localeCompare(b.category);
        break;
      case 'responses':
        comparison = (a.responses || 0) - (b.responses || 0);
        break;
      case 'views':
        comparison = (a.views || 0) - (b.views || 0);
        break;
      case 'uses':
        comparison = (a.uses || 0) - (b.uses || 0);
        break;
      default: // lastModified
        comparison = new Date(a.lastModified) - new Date(b.lastModified);
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      draft: 'bg-gray-100 text-gray-800',
      review: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-red-100 text-red-800',
      template: 'bg-blue-100 text-blue-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      active: 'Active',
      draft: 'Draft',
      review: 'Under Review',
      archived: 'Archived',
      template: 'Template'
    };
    return statusLabels[status] || status;
  };

  const toggleFavorite = (formId) => {
    setForms(prev => ({
      ...prev,
      [activeTab]: prev[activeTab].map(form =>
        form.id === formId ? { ...form, isFavorite: !form.isFavorite } : form
      )
    }));
  };

  const duplicateForm = (formId) => {
    const formToDuplicate = getCurrentForms().find(form => form.id === formId);
    if (formToDuplicate) {
      const duplicatedForm = {
        ...formToDuplicate,
        id: Date.now(),
        title: `${formToDuplicate.title} (Copy)`,
        status: 'draft',
        responses: 0,
        views: 0,
        lastModified: new Date().toISOString()
      };
      
      setForms(prev => ({
        ...prev,
        created: [duplicatedForm, ...prev.created]
      }));
    }
  };

  const deleteForm = (formId) => {
    if (window.confirm('Are you sure you want to delete this form?')) {
      setForms(prev => ({
        ...prev,
        [activeTab]: prev[activeTab].filter(form => form.id !== formId)
      }));
    }
  };

  const tabs = [
    { id: 'created', name: 'Created by Me', count: forms.created?.length || 0 },
    { id: 'collaborating', name: 'Collaborating', count: forms.collaborating?.length || 0 },
    { id: 'templates', name: 'Templates', count: forms.templates?.length || 0 }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">My Forms</h1>
                <p className="mt-2 text-sm text-gray-700">
                  Manage your forms, templates, and collaborations
                </p>
              </div>
              <div className="mt-4 sm:mt-0">
                <Link
                  to="/forms/create"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                  Create New Form
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  {tab.name}
                  <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search forms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center space-x-4">
              {/* Filters Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                Filters
              </button>

              {/* Sort */}
              <div className="relative">
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  className="block appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm leading-5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="lastModified-desc">Recently Updated</option>
                  <option value="lastModified-asc">Oldest First</option>
                  <option value="title-asc">Title A-Z</option>
                  <option value="title-desc">Title Z-A</option>
                  <option value="category-asc">Category A-Z</option>
                  {activeTab !== 'templates' && (
                    <>
                      <option value="responses-desc">Most Responses</option>
                      <option value="views-desc">Most Views</option>
                    </>
                  )}
                  {activeTab === 'templates' && (
                    <option value="uses-desc">Most Used</option>
                  )}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <ChevronDownIcon className="h-4 w-4" />
                </div>
              </div>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                    <option value="review">Under Review</option>
                    {activeTab === 'templates' && (
                      <option value="template">Template</option>
                    )}
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setSearchTerm('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Forms Grid */}
        {sortedForms.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchTerm || statusFilter !== 'all' ? 'No forms found' : 'No forms yet'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : activeTab === 'created' 
                ? 'Get started by creating your first form.'
                : activeTab === 'collaborating'
                ? 'You are not currently collaborating on any forms.'
                : 'You have not created any templates yet.'
              }
            </p>
            {activeTab === 'created' && !searchTerm && statusFilter === 'all' && (
              <div className="mt-6">
                <Link
                  to="/forms/create"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                  Create Your First Form
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedForms.map((form) => (
              <div key={form.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center flex-1">
                      <DocumentTextIcon className="h-8 w-8 text-blue-500" />
                      <div className="ml-3 flex-1 min-w-0">
                        <Link
                          to={`/forms/${form.id}`}
                          className="text-lg font-medium text-gray-900 hover:text-blue-600 block truncate"
                        >
                          {form.title}
                        </Link>
                        <p className="text-sm text-gray-500">v{form.version}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                      <button
                        onClick={() => toggleFavorite(form.id)}
                        className="text-gray-400 hover:text-yellow-500"
                      >
                        {form.isFavorite ? (
                          <StarIconSolid className="h-5 w-5 text-yellow-400" />
                        ) : (
                          <StarIcon className="h-5 w-5" />
                        )}
                      </button>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(form.status)}`}>
                        {getStatusLabel(form.status)}
                      </span>
                    </div>
                  </div>
                  
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                    {form.description}
                  </p>
                  
                  <div className="mt-4 flex items-center text-sm text-gray-500">
                    <FolderIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                    {form.category}
                  </div>
                  
                  {activeTab === 'collaborating' && form.createdBy && (
                    <div className="mt-2 text-sm text-gray-500">
                      Created by {form.createdBy} • {form.role}
                    </div>
                  )}
                  
                  <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      {activeTab === 'templates' ? (
                        <div className="flex items-center">
                          <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1" />
                          {form.uses} uses
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center">
                            <EyeIcon className="h-4 w-4 mr-1" />
                            {form.views}
                          </div>
                          <div className="flex items-center">
                            <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1" />
                            {form.responses || 0}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {formatDate(form.lastModified)}
                    </div>
                  </div>
                  
                  {form.tags && (
                    <div className="mt-4 flex flex-wrap gap-1">
                      {form.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="mt-6 flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/forms/${form.id}`}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <EyeIcon className="h-3 w-3 mr-1" />
                        View
                      </Link>
                      
                      {activeTab === 'created' && (
                        <Link
                          to={`/forms/${form.id}/edit`}
                          className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <PencilIcon className="h-3 w-3 mr-1" />
                          Edit
                        </Link>
                      )}
                      
                      {activeTab !== 'templates' && (
                        <Link
                          to={`/forms/${form.id}/analytics`}
                          className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <ChartBarIcon className="h-3 w-3 mr-1" />
                          Stats
                        </Link>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => duplicateForm(form.id)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Duplicate"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                      
                      {activeTab === 'created' && (
                        <button
                          onClick={() => deleteForm(form.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
      <Footer />
    </>
  );
};

export default MyForms;
